class RelatedProductsProgressive extends HTMLElement {
  #items = [];
  #visibleCount = 0;
  #moreButton;
  #controller;

  connectedCallback() {
    this.refresh();
  }

  // Shopify hydration preserves this element while replacing its card markup.
  refresh() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#items = [];
    this.#prepareItems();
    if (this.#items.length === 0) return;

    this.#moreButton = this.querySelector('[data-related-products-more]');
    this.#visibleCount = Math.min(Math.max(this.#visibleCount, this.#initialCount()), this.#items.length);
    this.#render();
    this.#bindEvents();
    this.dataset.ready = 'true';
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #prepareItems() {
    const seenIds = new Set();
    const currentProductId = this.dataset.currentProductId;
    const max = Number(this.dataset.max) || 20;

    for (const item of this.querySelectorAll('.related-products__item')) {
      const productId = item.dataset.productId;

      if (
        !productId ||
        productId === currentProductId ||
        seenIds.has(productId) ||
        this.#items.length >= max
      ) {
        item.remove();
        continue;
      }

      seenIds.add(productId);
      item.dataset.position = String(this.#items.length + 1);
      this.#items.push(item);
    }
  }

  #initialCount() {
    return 6;
  }

  #render() {
    this.#items.forEach((item, index) => {
      item.hidden = index >= this.#visibleCount;
    });

    if (!this.#moreButton) return;

    const remaining = this.#items.length - this.#visibleCount;
    this.#moreButton.hidden = remaining <= 0;
    if (remaining > 0) {
      this.#moreButton.textContent = (this.dataset.moreLabel || "Show [count] more").replace("[count]", String(Math.min(4, remaining)));
    }
  }

  #bindEvents() {
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const moreButton = event.target.closest('[data-related-products-more]');
      if (moreButton) {
        const nextItem = this.#items[this.#visibleCount];
        this.#visibleCount = Math.min(this.#visibleCount + 4, this.#items.length);
        this.#render();
        const status = this.querySelector('[data-related-products-status]');
        if (status) status.textContent = (this.dataset.statusLabel || "[count] artworks shown.").replace("[count]", String(this.#visibleCount));
        // Move focus to newly revealed content, including when the button disappears.
        const nextLink = nextItem?.querySelector("a[href]");
        if (nextLink instanceof HTMLElement) nextLink.focus({ preventScroll: true });
        this.#publish('frest_related_products_expand', {
          visibleProducts: this.#visibleCount,
        });
        return;
      }

      const link = event.target.closest('.related-products__item a');
      if (!link) return;
      const item = link.closest('.related-products__item');
      this.#publish('frest_related_product_click', {
        recommendedProductId: item?.dataset.productId,
        position: Number(item?.dataset.position),
        source: item?.dataset.recommendationSource,
      });
    }, { signal: this.#controller.signal });
  }

  #publish(name, data) {
    try {
      window.Shopify?.analytics?.publish(name, {
        sourceProductId: this.dataset.currentProductId,
        collectionHandle: this.dataset.collectionHandle,
        ...data,
      });
    } catch (error) {
      console.debug(`Analytics event ${name} was not published`, error);
    }
  }
}

if (!customElements.get('related-products-progressive')) {
  customElements.define('related-products-progressive', RelatedProductsProgressive);
}
